/**
 * `kind: 'number'`. Submits a number (not a string): core validates the range, so the
 * field only guards against sending something that is not a finite number. `request.mine`
 * (the echo) exists only if the ask set `revealSubmittedToSelf`.
 */

import { useEffect, useRef, useState } from 'react';
import type { InputSpec } from '@gsf/contract';
import type { RendererProps } from '../renderer';

/** Keep only numeric characters: digits, one leading minus, one decimal point. */
function onlyNumeric(raw: string): string {
  let s = raw.replace(/[^\d.-]/gu, '');
  s = s.replace(/(?!^)-/gu, ''); // a minus only at the front
  const dot = s.indexOf('.');
  if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./gu, ''); // one dot
  return s;
}

/** The entered text as a finite number, or null when it is blank or not a number. */
function parsed(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '' || trimmed === '-' || trimmed === '.') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

export function NumberInput({
  request,
  submit,
  disabled,
}: RendererProps<Extract<InputSpec, { kind: 'number' }>>) {
  const submitted = typeof request.mine?.value === 'number' ? request.mine.value : null;
  const [text, setText] = useState(submitted === null ? '' : String(submitted));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const value = parsed(text);

  return (
    <>
      {request.prompt ? <p className="play-clue">{request.prompt}</p> : null}
      <form
        className="play-answer-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (value !== null) submit(value);
        }}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={text}
          // Strip non-numeric characters as they are typed, so only a number can be entered.
          onChange={(event) => setText(onlyNumeric(event.target.value))}
          placeholder="Enter a number"
          autoComplete="off"
          disabled={disabled}
        />
        <p className="faint">Numbers only.</p>
        <button className="btn btn-primary btn-lg" type="submit" disabled={disabled || value === null}>
          {submitted !== null ? 'Update answer' : 'Submit'}
        </button>
        {submitted !== null ? (
          <p className="faint">
            Submitted: {submitted}
            {request.allowRevision ? ' — you can change it.' : ''}
          </p>
        ) : null}
      </form>
    </>
  );
}
